import { APP_NAME } from '../utils/branding';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaLock } from 'react-icons/fa';
import { authAPI } from '../services/api';
import './PasswordRecovery.css';

const PasswordRecovery = ({ reset = false }) => {
  const [searchParams] = useSearchParams();
  const resetEmail = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const incompleteLink = reset && (!resetEmail || !token);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown(seconds => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  // Clear the previous form's result when following a recovery link in this app.
  useEffect(() => {
    setMessage('');
    setError('');
    setPassword('');
    setConfirmation('');
  }, [reset, resetEmail, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || incompleteLink || (!reset && cooldown > 0)) return;
    setError('');

    if (reset && password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (reset && password !== confirmation) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setMessage('');
    setLoading(true);
    try {
      const response = reset
        ? await authAPI.resetPassword({
            email: resetEmail,
            token,
            password,
            password_confirmation: confirmation,
          })
        : await authAPI.forgotPassword(email.trim());
      setMessage(response.message || (reset
        ? 'Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.'
        : 'Si un compte correspond à cette adresse, un lien de réinitialisation vous sera envoyé.'));
      if (reset) {
        setPassword('');
        setConfirmation('');
      } else {
        setCooldown(60);
      }
    } catch (err) {
      const validationMessage = Object.values(err.errors || {}).flat()[0];
      setError(validationMessage || (err.message === 'Too Many Attempts.'
        ? 'Trop de tentatives. Veuillez patienter une minute avant de réessayer.'
        : err.message || 'Une erreur est survenue. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="password-recovery-page">
      <section className="password-recovery-card" aria-labelledby="password-recovery-title">
        <div className={`password-recovery-icon ${message ? 'success' : ''}`} aria-hidden="true">
          {message ? <FaCheckCircle /> : <FaLock />}
        </div>
        <h1 id="password-recovery-title">
          {reset ? 'Nouveau mot de passe' : 'Mot de passe oublié ?'}
        </h1>
        {!message && !incompleteLink && (
          <p>{reset
            ? `Choisissez un nouveau mot de passe pour votre compte ${APP_NAME}.`
            : 'Saisissez votre adresse e-mail pour recevoir un lien de réinitialisation.'}</p>
        )}

        {reset && !message && !incompleteLink && (
          <a className="password-recovery-link" href={`photochap://reset-password?${new URLSearchParams({ email: resetEmail, token }).toString()}`}>
            Ouvrir dans l’application {APP_NAME}
          </a>
        )}

        {message && <div className="password-recovery-success" role="status">{message}</div>}
        {(error || incompleteLink) && (
          <div className="password-recovery-error" role="alert">
            {incompleteLink ? 'Ce lien de réinitialisation est incomplet. Demandez un nouveau lien.' : error}
          </div>
        )}

        {!incompleteLink && !(reset && message) && (
          <form onSubmit={handleSubmit} aria-busy={loading}>
            {reset ? (
              <>
                <div className="password-recovery-field">
                  <label htmlFor="reset-email">Adresse e-mail</label>
                  <input id="reset-email" type="email" autoComplete="username" value={resetEmail} readOnly />
                </div>
                <div className="password-recovery-field">
                  <label htmlFor="new-password">Nouveau mot de passe</label>
                  <input id="new-password" name="password" type="password" autoComplete="new-password"
                    value={password} onChange={event => setPassword(event.target.value)}
                    minLength={6} required disabled={loading} aria-describedby="password-recovery-hint" />
                  <small id="password-recovery-hint">Au moins 6 caractères</small>
                </div>
                <div className="password-recovery-field">
                  <label htmlFor="password-confirmation">Confirmer le mot de passe</label>
                  <input id="password-confirmation" name="password_confirmation" type="password" autoComplete="new-password"
                    value={confirmation} onChange={event => setConfirmation(event.target.value)}
                    minLength={6} required disabled={loading} />
                </div>
              </>
            ) : (
              <div className="password-recovery-field">
                <label htmlFor="recovery-email">Adresse e-mail</label>
                <input id="recovery-email" name="email" type="email" autoComplete="email" placeholder="vous@exemple.com"
                  value={email} onChange={event => setEmail(event.target.value)} maxLength={255} required disabled={loading} />
              </div>
            )}
            <button type="submit" className="password-recovery-submit" disabled={loading || (!reset && cooldown > 0)}>
              {loading ? 'Envoi en cours…' : reset ? 'Réinitialiser mon mot de passe'
                : cooldown > 0 ? `Renvoyer dans ${cooldown} s` : message ? 'Renvoyer le lien' : 'Envoyer le lien'}
            </button>
          </form>
        )}

        {reset && !message && (
          <Link to="/forgot-password" className="password-recovery-link">Demander un nouveau lien</Link>
        )}
        <Link to="/login" className="password-recovery-link"><FaArrowLeft aria-hidden="true" /> Retour à la connexion</Link>
      </section>
    </div>
  );
};

export default PasswordRecovery;
